import {
  REPEAT_SET_NUMBER,
  TOOL_SET_NUMBER,
  UNCATEGORIZED_SET_NUMBER,
  type TaskEditorState,
  type TaskMetaUpdate,
} from "./taskEditorUtils";

// 재정렬 시 부여하는 sort_order 간격. 조밀 정수 reindex (1000, 2000, …).
export const TASK_REORDER_STEP = 1000;

export interface TaskEditorListSection {
  key: string;
  label: string;
  set_number: number; // 미분류 = UNCATEGORIZED_SET_NUMBER(9999)
  tasks: TaskEditorState[];
}

function compareTaskEditorTasks(left: TaskEditorState, right: TaskEditorState) {
  // 같은 세트 안에서는 sort_order 오름차순이 1차 기준.
  // sort_order 미설정(레거시/신규)은 뒤로 보내고, 동률이면 title→task_id 로 안정화.
  const leftOrder =
    typeof left.sort_order === "number"
      ? left.sort_order
      : Number.POSITIVE_INFINITY;
  const rightOrder =
    typeof right.sort_order === "number"
      ? right.sort_order
      : Number.POSITIVE_INFINITY;

  if (leftOrder !== rightOrder) {
    return leftOrder - rightOrder;
  }

  const titleCompare = left.title.localeCompare(right.title, "ko");

  if (titleCompare !== 0) {
    return titleCompare;
  }

  return left.task_id.localeCompare(right.task_id, "ko");
}

export function getTaskEditorSetSectionLabel(set_number: number) {
  if (set_number === TOOL_SET_NUMBER) return "툴";
  if (set_number === REPEAT_SET_NUMBER) return "반복";
  return set_number === UNCATEGORIZED_SET_NUMBER
    ? "미분류"
    : `세트 ${set_number}`;
}

// 섹션 표시 순서 — 일반 세트(오름차순) → 툴 → 반복 → 미분류.
// sentinel 숫자값(반복 9997 < 툴 9998)이 표시 순서와 다르므로 명시 rank 로 정렬한다.
function getSectionDisplayRank(set_number: number) {
  if (set_number === TOOL_SET_NUMBER) return Number.MAX_SAFE_INTEGER - 2;
  if (set_number === REPEAT_SET_NUMBER) return Number.MAX_SAFE_INTEGER - 1;
  if (set_number === UNCATEGORIZED_SET_NUMBER) return Number.MAX_SAFE_INTEGER;
  return set_number;
}

// 유형별 필수 메타가 비었거나 참조가 깨진 경우(참조 활동지 삭제·유형 변경 등) 의
// 사유 문구. 문제 없으면 undefined — 목록 행의 유형 chip 경고 표시용.
export function getTaskMetaIssue(
  task: TaskEditorState,
  tasksByTaskId: Record<string, TaskEditorState>
): string | undefined {
  if (task.task_type === "tool") {
    if (!task.prerequisite_task_id) return "선행 활동지가 설정되지 않았어요";
    if (!tasksByTaskId[task.prerequisite_task_id]) {
      return "선행 활동지가 삭제되었어요";
    }
    return undefined;
  }
  if (task.task_type === "empty") {
    if (!task.linked_tool_task_id) return "연결 툴이 설정되지 않았어요";
    const linked = tasksByTaskId[task.linked_tool_task_id];
    if (!linked) return "연결 툴 활동지가 삭제되었어요";
    if (linked.task_type !== "tool") {
      return "연결된 활동지가 더 이상 툴이 아니에요";
    }
    return undefined;
  }
  if (task.task_type === "repeat") {
    if (!task.repeat_interval_weeks) return "반복 주기가 설정되지 않았어요";
    return undefined;
  }
  return undefined;
}

// 신규 활동지를 해당 세트의 맨 끝에 둘 sort_order. 세트 내 최대값 + STEP.
// (메타 모달 생성 시 sort_order 가 NOT NULL 필수라 클라이언트가 부여.)
export function getNextSortOrderForSet(
  tasks: TaskEditorState[],
  set_number: number
): number {
  const max = tasks.reduce((acc, task) => {
    if (
      task &&
      task.set_number === set_number &&
      typeof task.sort_order === "number"
    ) {
      return Math.max(acc, task.sort_order);
    }
    return acc;
  }, 0);
  return max + TASK_REORDER_STEP;
}

// 세트당 Key 활동지는 1개 — 이미 Key 활동지가 있는 세트 번호 목록.
// 메타 모달의 Key 충돌 검사용. excludeTaskId 는 수정 중인 활동지 자신을 제외
// (자신이 그 세트의 Key 여도 충돌로 보지 않기 위함). 생성 모드는 excludeTaskId 없음.
export function getKeyActivitySetNumbers(
  tasks: TaskEditorState[],
  excludeTaskId?: string
): number[] {
  const setNumbers = new Set<number>();
  tasks.forEach((task) => {
    if (task && task.is_key_activity && task.task_id !== excludeTaskId) {
      setNumbers.add(task.set_number);
    }
  });
  return Array.from(setNumbers);
}

// 드래그 한 번의 결과를 task-meta/save-all 용 update 배치로 변환.
//  - draggedTaskId: 옮기는 활동지
//  - targetSetNumber: 떨어뜨린 섹션 (미분류 = 9999)
//  - beforeTaskId: 이 활동지 "앞"에 삽입. null = 섹션 맨 끝.
// 드롭 위치를 task_id 기준으로 받으면 드래그 대상이 같은 섹션에 있을 때의
// 인덱스 보정이 필요 없다 (대상은 sibling 목록에서 이미 제외되므로).
// 타깃 섹션만 조밀 reindex 하고, (set_number, sort_order) 가 실제 바뀐 활동지만 emit.
// 소스 섹션의 남은 활동지들은 상대 순서가 유지되므로(틈만 생김) 갱신 불필요.
export function computeListReorderUpdates(
  tasks: TaskEditorState[],
  draggedTaskId: string,
  targetSetNumber: number,
  beforeTaskId: string | null
): TaskMetaUpdate[] {
  const dragged = tasks.find((task) => task?.task_id === draggedTaskId);
  if (!dragged) {
    return [];
  }

  const targetSiblings = tasks
    .filter(
      (task) =>
        task &&
        task.task_id !== draggedTaskId &&
        task.set_number === targetSetNumber
    )
    .sort(compareTaskEditorTasks);

  const beforeIndex =
    beforeTaskId === null
      ? targetSiblings.length
      : targetSiblings.findIndex((task) => task.task_id === beforeTaskId);
  const insertIndex = beforeIndex < 0 ? targetSiblings.length : beforeIndex;

  const reordered = [
    ...targetSiblings.slice(0, insertIndex),
    dragged,
    ...targetSiblings.slice(insertIndex),
  ];

  const updates: TaskMetaUpdate[] = [];

  reordered.forEach((task, index) => {
    const nextSortOrder = (index + 1) * TASK_REORDER_STEP;
    const sameSet = task.set_number === targetSetNumber;
    const sameOrder = task.sort_order === nextSortOrder;

    if (sameSet && sameOrder) {
      return;
    }

    updates.push({
      task_id: task.task_id,
      title: task.title,
      set_number: targetSetNumber,
      is_key_activity: Boolean(task.is_key_activity),
      sort_order: nextSortOrder,
      // full replace — 유형/선행 활동지/연결 툴/반복 주기를 함께 보내지 않으면 백엔드에서 지워진다.
      task_type: task.task_type ?? "basic",
      ...(task.prerequisite_task_id
        ? { prerequisite_task_id: task.prerequisite_task_id }
        : {}),
      ...(task.linked_tool_task_id
        ? { linked_tool_task_id: task.linked_tool_task_id }
        : {}),
      ...(task.repeat_interval_weeks
        ? { repeat_interval_weeks: task.repeat_interval_weeks }
        : {}),
    });
  });

  return updates;
}

// 리프레시 후 초기 선택 활동지 결정.
//  - 저장된 마지막 선택(savedTaskId)이 목록에 살아있으면 그대로 복원.
//  - 없거나 삭제됐으면 표시 순서(섹션 rank → sort_order) 첫 활동지로 폴백.
//    (Object.values 순서 = 서버 응답 순서라 화면 맨 위와 다를 수 있다.)
export function resolveInitialActiveTaskId(
  savedTaskId: string | null,
  tasks: TaskEditorState[],
  tasksByTaskId: Record<string, TaskEditorState>
): string | undefined {
  if (savedTaskId && tasksByTaskId[savedTaskId]) {
    return savedTaskId;
  }
  return buildTaskEditorListSections(tasks)[0]?.tasks[0]?.task_id;
}

export function buildTaskEditorListSections(
  tasks: TaskEditorState[]
): TaskEditorListSection[] {
  if (!Array.isArray(tasks)) {
    return [];
  }

  const groupedTasks = new Map<string, TaskEditorListSection>();

  tasks.forEach((task) => {
    if (!task) {
      return;
    }

    const key = `set-${task.set_number}`;
    const existingSection = groupedTasks.get(key);

    if (existingSection) {
      existingSection.tasks.push(task);
      return;
    }

    groupedTasks.set(key, {
      key,
      label: getTaskEditorSetSectionLabel(task.set_number),
      set_number: task.set_number,
      tasks: [task],
    });
  });

  // 일반 세트는 set_number 오름차순, sentinel 섹션(툴/반복/미분류)은 표시 rank 로 맨 뒤.
  return Array.from(groupedTasks.values())
    .map((section) => ({
      ...section,
      tasks: [...section.tasks].sort(compareTaskEditorTasks),
    }))
    .sort(
      (left, right) =>
        getSectionDisplayRank(left.set_number) -
        getSectionDisplayRank(right.set_number)
    );
}
